import {BalanceResult, ChemicalCompound, ChemicalEquation, ChemicalNode, ElementCount, Matrix} from './types';
import {MatrixUtils} from './matrix';
import Fraction from 'fraction.js';

/**
 * 化学方程式配平器
 */
export class ChemicalBalancer {
    /**
     * 配平化学方程式
     */
    balance(equation: ChemicalEquation): BalanceResult {
        try {
            // 获取所有化合物
            const compounds = [...equation.reactants, ...equation.products];

            // 提取所有元素
            const elements = this.extractElements(compounds);

            if (elements.length === 0) {
                return {success: false, equations: [], error: 'No elements found'};
            }

            // 构建系数矩阵
            const matrix = this.buildCoefficientMatrix(compounds, elements, equation);

            // 求解零空间
            const nullSpace = MatrixUtils.nullSpace(matrix);

            if (nullSpace.length === 0) {
                return {success: false, equations: [], error: 'No solution found'};
            }

            // 生成配平结果
            const balancedEquations = this.generateBalancedEquations(equation, nullSpace);

            return {
                success: true,
                equations: balancedEquations
            };
        } catch (error) {
            return {
                success: false,
                equations: [],
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * 提取所有元素（包括电荷）
     */
    private extractElements(compounds: ChemicalCompound[]): string[] {
        const elementSet = new Set<string>();

        for (const compound of compounds) {
            const elements = this.getCompoundElements(compound);
            for (const element of Object.keys(elements)) {
                elementSet.add(element);
            }
        }

        return Array.from(elementSet).sort();
    }

    /**
     * 获取化合物中的元素计数
     */
    private getCompoundElements(compound: ChemicalCompound): ElementCount {
        const elements: ElementCount = {};

        for (const node of compound.nodes) {
            const nodeElements = this.getNodeElements(node);
            for (const [element, count] of Object.entries(nodeElements)) {
                elements[element] = (elements[element] || 0) + count;
            }
        }

        return elements;
    }

    /**
     * 获取节点中的元素计数
     */
    private getNodeElements(node: ChemicalNode): ElementCount {
        const elements: ElementCount = {};

        switch (node.type) {
            case 'element':
                elements[node.symbol] = node.count;
                break;

            case 'group':
                for (const childNode of node.elements) {
                    const childElements = this.getNodeElements(childNode);
                    for (const [element, count] of Object.entries(childElements)) {
                        elements[element] = (elements[element] || 0) + count * node.count;
                    }
                }
                break;

            case 'ion':
                // 处理离子中的元素
                for (const childNode of node.elements) {
                    const childElements = this.getNodeElements(childNode);
                    for (const [element, count] of Object.entries(childElements)) {
                        elements[element] = (elements[element] || 0) + count * node.count;
                    }
                }
                // 添加电荷作为特殊"元素"
                if (node.charge !== 0) {
                    elements['CHARGE'] = (elements['CHARGE'] || 0) + node.charge * node.count;
                }
                break;
        }

        return elements;
    }

    /**
     * 构建系数矩阵
     */
    private buildCoefficientMatrix(compounds: ChemicalCompound[], elements: string[], equation: ChemicalEquation): Matrix {
        const matrix: Matrix = [];

        // 为每个元素创建一行
        for (const element of elements) {
            const row: number[] = [];

            for (const compound of compounds) {
                const compoundElements = this.getCompoundElements(compound);
                row.push(compoundElements[element] || 0);
            }

            matrix.push(row);
        }

        // 调整反应物和生成物的符号
        const reactantCount = equation.reactants.length;

        for (let i = 0; i < matrix.length; i++) {
            for (let j = reactantCount; j < compounds.length; j++) {
                matrix[i][j] = -matrix[i][j];
            }
        }

        return matrix;
    }

    /**
     * 获取生成物数量（假设反应物和生成物数量相等或接近）
     */
    private getProductCount(totalCount: number): number {
        return Math.floor(totalCount / 2);
    }

    /**
     * 生成配平后的方程式
     */
    private generateBalancedEquations(originalEquation: ChemicalEquation, nullSpace: Matrix): ChemicalEquation[] {
        const equations: ChemicalEquation[] = [];

        for (const solution of nullSpace) {
            // 确保系数为正整数
            const coefficients = this.normalizeCoefficients(solution);

            // 应用系数到原方程式
            const balancedEquation = this.applyCoefficients(originalEquation, coefficients);
            equations.push(balancedEquation);
        }

        return equations;
    }

// Helper to safely convert BigInt to Number if it's within safe range
// or keep as BigInt if operations will handle it.
// For coefficients, we usually want Numbers if they fit.
    private convertToNumberIfSafe(val: number | bigint): number {
        if (typeof val === 'bigint') {
            if (val > BigInt(Number.MAX_SAFE_INTEGER) || val < BigInt(Number.MIN_SAFE_INTEGER)) {
                console.warn(`BigInt value ${val} is too large to be safely converted to Number and may lose precision.`);
                // Decide on error handling or fallback if precision loss is critical
            }
            return Number(val);
        }
        return val;
    }

// BigInt-aware absolute value
    private absBigIntAware(val: number | bigint): number | bigint {
        if (typeof val === 'bigint') {
            return val < 0n ? -val : val;
        }
        return Math.abs(val);
    }


    private normalizeCoefficients(solution: (number | bigint)[]): number[] {
        // Check for all-zero solution (including BigInt zero)
        if (solution.every(x => x === 0 || x === 0n)) {
            console.warn("NormalizeCoefficients received an all-zero solution vector.");
            return solution.map(() => 1); // Or other appropriate handling
        }

        // Make all coefficients positive (BigInt aware)
        // And convert to Number if they are BigInts from solution, assuming they fit
        let processingSolutionNumbers: number[] = solution.map(x => {
            let absVal = this.absBigIntAware(x);
            return this.convertToNumberIfSafe(absVal);
        });

        // The rest of fraction logic assumes input numbers are standard numbers.
        // If processingSolutionNumbers could still contain very large numbers that fraction.js
        // would turn into BigInts internally, then .n might again be BigInt.
        const fractions = processingSolutionNumbers.map(x => new Fraction(x));

        const nonZeroFractions = fractions.filter(f => !f.equals(0));
        if (nonZeroFractions.length === 0) {
            return processingSolutionNumbers.map(() => 1); // All were zero
        }

        let lcmDenominator = new Fraction(1);
        for (const frac of nonZeroFractions) {
            // frac.d from fraction.js is a Number (denominator)
            lcmDenominator = lcmDenominator.lcm(new Fraction(frac.d));
        }

        // frac.mul(lcmDenominator).n can be a Number or BigInt from fraction.js
        const integerCoefficientsMixed: (number | bigint)[] = fractions.map(frac => {
            const multiplied = frac.mul(lcmDenominator);
            // multiplied.n is the numerator from fraction.js, could be BigInt
            return multiplied.n;
        });

        // Convert all to Number for GCD and final output, ensuring they are positive
        const integerCoefficients: number[] = integerCoefficientsMixed.map(val => {
            return this.convertToNumberIfSafe(this.absBigIntAware(val));
        });

        const nonZeroIntegerCoefficients = integerCoefficients.filter(x => x !== 0);
        if (nonZeroIntegerCoefficients.length === 0) {
            return integerCoefficients.map(() => 1);
        }

        // findGCD now expects and works with Numbers
        const commonDivisor = this.findGCD(nonZeroIntegerCoefficients);

        if (commonDivisor > 0 && commonDivisor !== Infinity) { // commonDivisor should be a finite number
            return integerCoefficients.map(x => x / commonDivisor);
        } else {
            return integerCoefficients; // Fallback if GCD is invalid
        }
    }


// findGCD and gcd should now primarily deal with Numbers
// Ensure inputs to these are Numbers after BigInts have been converted.
    private findGCD(numbers: number[]): number {
        if (numbers.length === 0) return 1;

        // Ensure numbers are positive integers. Math.round is for safety if inputs aren't perfectly integer.
        const positiveNumbers = numbers.map(x => Math.abs(Math.round(x))).filter(x => x !== 0);
        if (positiveNumbers.length === 0) return 1;

        let result = positiveNumbers[0];
        for (let i = 1; i < positiveNumbers.length; i++) {
            result = this.gcd(result, positiveNumbers[i]);
        }
        return result;
    }

    private gcd(a: number, b: number): number {
        // Ensure inputs are positive integers
        a = Math.abs(Math.round(a));
        b = Math.abs(Math.round(b));

        if (a === 0) return b;
        if (b === 0) return a;
        if (isNaN(a) || isNaN(b) || !isFinite(a) || !isFinite(b)) { // Robustness
            console.warn(`Invalid input to gcd: a=${a}, b=${b}`);
            return 1; // Or throw error
        }

        while (b !== 0) {
            const temp = b;
            b = a % b;
            a = temp;
        }
        return a;
    }

    /**
     * 应用系数到方程式
     */
    private applyCoefficients(equation: ChemicalEquation, coefficients: number[]): ChemicalEquation {
        const allCompounds = [...equation.reactants, ...equation.products];

        if (coefficients.length !== allCompounds.length) {
            throw new Error('Coefficient count mismatch');
        }

        const reactantCount = equation.reactants.length;

        const newReactants = equation.reactants.map((compound, index) => ({
            ...compound,
            coefficient: coefficients[index]
        }));

        const newProducts = equation.products.map((compound, index) => ({
            ...compound,
            coefficient: coefficients[reactantCount + index]
        }));

        return {
            reactants: newReactants,
            products: newProducts
        };
    }
}